import React, { useState } from 'react'
import { Box, Button, Menu, MenuItem, styled, Link } from '@mui/material'
import { EllipsisIcon } from '@rsmus/myrsm-icons'
import { useTranslation, Trans } from 'react-i18next'
import type { UserAssignmentsViewModel } from '@rsmus/ecp-userservice'
import { UserApplicationData } from '../../store/userManagement/userManagementSearchService'
import tokens from '../../styles/tokens.json'
import { Styles } from '../../types'
import api from '../../api'

import UserManagementAssignmentModal from './UserManagementAssignmentModal'
import UserManagementConfirmRevokeAll from './UserManagementConfirmRevokeAll'
import CustomSuccessAlert from '../forms/Alert/CustomSuccessAlert/CustomSuccessAlert'
import CustomErrorAlert from '../forms/Alert/CustomErrorAlert/CustomErrorAlert'

/**
 * Styles for the UserManagementActionMenu component
 * Following the ActionMenu.tsx pattern for consistency
 */
const styles: Styles = {
  actions: {
    position: 'relative',
  },
  actionsButton: {
    display: 'flex',
    justifyContent: 'center',
    boxSizing: 'content-box',
    margin: '0rem',
    padding: '0.5rem',
    minWidth: 'unset',
    width: '1.5rem',
    height: '1.5rem',
  },
  actionsMenu: {
    '& .MuiPaper-elevation': {
      marginTop: '2.25rem',
      boxShadow: '0rem 0rem 0.5rem rgba(0, 0, 0, 0.125)',
    },
    '& .MuiMenu-list': {
      border: `0.0625rem solid ${tokens.colors.halfWhite}`,
      borderBottom: 'none',
      width: 'fit-content',
    },
    '& .MuiMenuItem-root': {
      borderBottom: `0.0625rem solid ${tokens.colors.halfWhite}`,
    },
  },
}

/**
 * Props interface for UserManagementActionMenu component
 */
interface UserManagementActionMenuProps {
  user: UserApplicationData
  dataTestId: string
}

/**
 * UserManagementActionMenu Component
 *
 * Renders a kebab menu (three vertical dots) for per-row user management actions.
 * Provides access to user management operations including:
 * - Manage user's access - Opens UserManagementAssignmentModal for editing tool-role assignments
 * - Revoke all access - Opens UserManagementAssignmentModal for revoking all user access
 *
 * Features:
 * - Integration with UserManagementAssignmentModal for assignment management
 * - Success and error toast notifications using CustomSuccessAlert and CustomErrorAlert
 * - Modal state management for opening/closing assignment modal
 * - Passes selected user information to modal component
 *
 * Accessibility Features:
 * - Full ARIA attribute support (aria-label, aria-controls, aria-haspopup, aria-expanded)
 * - Keyboard navigation (Tab, Enter/Space, Arrow keys, Escape)
 * - Automatic focus management via MUI Menu component
 * - Screen reader support with proper labels and announcements
 *
 * Internationalization:
 * - All strings via useTranslation hook with UserManagement.ActionMenu namespace
 * - Supports English and Spanish translations
 * - Dynamic user name interpolation using {{name}} syntax
 *
 * @param user - UserApplicationData object containing user information
 * @param dataTestId - Unique identifier for testing purposes
 */
const UserManagementActionMenu = ({
  user,
  dataTestId,
}: UserManagementActionMenuProps) => {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isMenuOpen = Boolean(anchorEl)
  const [openManageModal, setOpenManageModal] = useState(false)
  const [selectedUserForManage, setSelectedUserForManage] =
    useState<UserApplicationData | null>(null)
  const [openRevokeDialog, setOpenRevokeDialog] = useState(false)
  const [revokeFirstName, setRevokeFirstName] = useState('')
  const [revokeLastName, setRevokeLastName] = useState('')
  const [isSubmittingRevoke, setIsSubmittingRevoke] = useState(false)
  const [showSuccessToast, setShowSuccessToast] = useState(false)
  const [showErrorToast, setShowErrorToast] = useState(false)

  /**
   * Opens the action menu
   * Sets the anchor element for proper menu positioning
   */
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  /**
   * Closes the action menu
   * Clears the anchor element
   */
  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  /**
   * Handles "Manage user's access" action
   * Opens the UserManagementAssignmentModal for the selected user
   */
  const handleManageAccess = () => {
    setSelectedUserForManage(user)
    setOpenManageModal(true)
    setAnchorEl(null)
  }

  /**
   * Handles "Revoke all access" action
   * Opens the UserManagementConfirmRevokeAll dialog for the selected user
   */
  const handleRevokeAccess = () => {
    setSelectedUserForManage(user)
    // Derive first and last name for confirmation dialog
    const rawName = user.name || ''
    let firstName = ''
    let lastName = ''

    if (rawName.includes(',')) {
      const parts = rawName.split(',').map((p) => p.trim())
      // "Last, First" format
      lastName = parts[0] || ''
      firstName = parts[1] || parts[0] || ''
    } else {
      const parts = rawName.split(' ').filter(Boolean)
      firstName = parts[0] || ''
      lastName = parts.slice(1).join(' ')
    }

    setRevokeFirstName(firstName)
    setRevokeLastName(lastName)
    setOpenRevokeDialog(true)
    setAnchorEl(null)
  }

  /**
   * Handle successful assignment update
   * Closes the modal and displays success toast notification
   */
  const handleModalSuccess = () => {
    setOpenManageModal(false)
    setShowSuccessToast(true)
  }

  /**
   * Handle assignment update error
   * Closes the modal and displays error toast notification with support ticket link
   */
  const handleModalError = () => {
    setOpenManageModal(false)
    setShowErrorToast(true)
  }

  /**
   * Handle confirmed revoke action from confirmation dialog
   * Calls UpdateUserAssignments with empty assignments array
   */
  const handleConfirmRevoke = async () => {
    if (!selectedUserForManage) {
      setOpenRevokeDialog(false)
      return
    }

    setIsSubmittingRevoke(true)

    try {
      const model: UserAssignmentsViewModel = {
        firstName: revokeFirstName,
        lastName: revokeLastName,
        email: selectedUserForManage.email,
        // Job title is not available in the grid row; send empty string.
        jobTitle: '',
        assignments: [],
      }

      await api.user.clientAdmin_UpdateUserAssignments(model)

      setShowSuccessToast(true)
    } catch (err) {
      setShowErrorToast(true)
    } finally {
      setIsSubmittingRevoke(false)
      setOpenRevokeDialog(false)
    }
  }

  const StyledLink = styled(Link)(({ theme }) => ({
    color: theme.palette.secondary.main,
    textDecoration: 'underline',
  }))

  return (
    <Box sx={styles.actions}>
      <Button
        sx={styles.actionsButton}
        id={`user-menu-button-${user.email}`}
        data-testid={dataTestId}
        aria-label={t('UserManagement.ActionMenu.ActionsLabel', {
          defaultValue: 'Actions for {{name}}',
          name: user.name,
        })}
        aria-controls={isMenuOpen ? `user-menu-${user.email}` : undefined}
        aria-haspopup="true"
        aria-expanded={isMenuOpen ? 'true' : undefined}
        disableRipple
        onClick={handleMenuOpen}>
        <EllipsisIcon />
      </Button>
      <Menu
        sx={styles.actionsMenu}
        id={`user-menu-${user.email}`}
        data-testid={`${dataTestId}_Menu`}
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{
          'aria-labelledby': `user-menu-button-${user.email}`,
        }}>
        <MenuItem
          data-testid={`${dataTestId}_ManageAccess`}
          onClick={handleManageAccess}>
          {t('UserManagement.ActionMenu.ManageAccess', {
            defaultValue: "Manage user's access",
          })}
        </MenuItem>
        <MenuItem
          data-testid={`${dataTestId}_RevokeAccess`}
          onClick={handleRevokeAccess}>
          {t('UserManagement.ActionMenu.RevokeAccess', {
            defaultValue: 'Revoke all access',
          })}
        </MenuItem>
      </Menu>

      {/* UserManagementAssignmentModal for managing user assignments */}
      <UserManagementAssignmentModal
        open={openManageModal}
        onClose={() => setOpenManageModal(false)}
        onSuccess={handleModalSuccess}
        onError={handleModalError}
        userEmail={selectedUserForManage?.email || ''}
        firstName={selectedUserForManage?.firstName || ''}
        lastName={selectedUserForManage?.lastName || ''}
      />

      {/* Confirm revoke-all dialog when invoked directly from the action menu */}
      <UserManagementConfirmRevokeAll
        open={openRevokeDialog}
        onClose={() => !isSubmittingRevoke && setOpenRevokeDialog(false)}
        onConfirm={handleConfirmRevoke}
        userFirstName={revokeFirstName}
        userLastName={revokeLastName}
        userEmail={selectedUserForManage?.email || ''}
      />

      {/* Success toast notification */}
      <CustomSuccessAlert
        header="UserManagement.ManageUser.SuccessHeader"
        message="UserManagement.ManageUser.SuccessMessage"
        open={showSuccessToast}
        close={() => setShowSuccessToast(false)}
        testId="Alert_ManageUser_Success"
      />

      {/* Error toast notification */}
      <CustomErrorAlert
        testId="Alert_ManageUser_Error"
        header="UserManagement.ManageUser.ErrorHeader"
        messageElement={
          <Trans
            i18nKey="UserManagement.ManageUser.ErrorMessage"
            defaults={t('UserManagement.ManageUser.ErrorMessage')}
            components={[
              <StyledLink
                sx={(theme) => ({
                  color: theme.palette.secondary.main,
                  textDecoration: 'underline',
                })}
                href="/contact-us/support-page"
                target="_self"
                rel="noopener noreferrer"
              />,
            ]}
          />
        }
        open={showErrorToast}
        close={() => setShowErrorToast(false)}
        disableAutoHide
      />
    </Box>
  )
}

export default UserManagementActionMenu
